import React from 'react';
import PropTypes from 'prop-types';
import { ActionCard, ActionCardSkeleton } from './ActionCards';
import { REPORT_METRIC_ACCENTS } from './actionCardColors';

let statCardSeq = 0;

/**
 * StatCard — the ONE presentational stat card (Launchpad, table strips, Reporting pages).
 *
 * It renders through ActionCard, so every surface shares the exact same shell: 12px
 * radius + border, flat accent top bar, header row (dot + UPPER-CASE label), 32px icon
 * top-right, dark number, chips row, soft-shadow selected state, compact spacing.
 *
 * Props
 *   label, value (node), accentColor, icon (Iconify name), tooltip
 *   chips   — same shape as ActionCard `splitChips` ({ text, type, onClick, … })
 *   delta   — { value: number|null, inverted?: bool, tooltip? } → TrendPill in the chips row
 *   onClick, selected, loading (skeleton)
 *   size    — 'md' (default) | 'sm' (compact, for dense grids)
 */
const StatCard = ({
    label,
    value = null,
    accentColor = REPORT_METRIC_ACCENTS.leads,
    icon = undefined,
    tooltip = undefined,
    chips = undefined,
    delta = undefined,
    onClick = undefined,
    selected = false,
    loading = false,
    size = 'md',
    valueSx = undefined,
    cardKey = undefined,
    filterCard = false,
    muted = false,
}) => {
    const keyRef = React.useRef(cardKey || `stat-${(statCardSeq += 1)}`);
    if (loading) return <ActionCardSkeleton />;
    const hasChips = Array.isArray(chips) && chips.length > 0;
    const card = {
        key: keyRef.current,
        count: 0,
        displayCount: value ?? '—',
        label,
        icon,
        accentColor,
        tooltip,
        urgencyState: 'neutral',
        size,
        delta,
        valueSx,
        // A selected 0 must not sprout the table-strip "Clear to see all" hint.
        clearFilterHint: false,
        nonInteractive: typeof onClick !== 'function',
        filterCard,
        // A quiet card (e.g. a zero count): the number and chips fade, the label stays readable.
        fadeEmphasis: Boolean(muted),
        ...(hasChips ? { isV2Split: true, splitChips: chips } : {}),
    };
    return <ActionCard card={card} onClick={onClick} isSelected={selected} />;
};

StatCard.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.node,
    accentColor: PropTypes.string,
    icon: PropTypes.string,
    tooltip: PropTypes.string,
    chips: PropTypes.array,
    delta: PropTypes.shape({ value: PropTypes.number, inverted: PropTypes.bool, tooltip: PropTypes.string }),
    valueSx: PropTypes.object,
    onClick: PropTypes.func,
    selected: PropTypes.bool,
    loading: PropTypes.bool,
    size: PropTypes.oneOf(['md', 'sm']),
    cardKey: PropTypes.string,
    // Card that filters a list: strong active state and a one-line label (ActionCard filterCard).
    filterCard: PropTypes.bool,
    // Fade the number and chips (FADED_OPACITY), e.g. for a zero count that needs no attention.
    muted: PropTypes.bool,
};

export default StatCard;
